import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

const STORAGE_KEY = "app-settings";

// The single source of truth for defaults. Any key missing from a persisted
// blob (older versions, partial imports) falls back to these values.
export const DEFAULT_SETTINGS = {
  units: {
    altitude: "ft", // "ft" | "m"
    speed: "kn", // "kn" | "mph" | "kmh"
    temp: "C", // "C" | "F"
    time: "local", // "utc" | "local"
  },
  refreshMs: 15000,
  showLabels: true,
  cluster: false,
  dataSource: "auto", // "auto" | "opensky" | "adsb"
  emergencySound: true,
  reduceMotion: false,
  density: "comfortable", // "comfortable" | "compact"
  fontScale: 1.0,
  preview3d: true, // show the 3D cockpit flight preview (loads Cesium on first open)
  previewTerrain: true, // use real world terrain in the preview when a token is set
};

// Deep-ish merge that only knows about our shallow-plus-units shape. Keeps the
// nested `units` object complete even if the stored value is partial.
function mergeWithDefaults(stored) {
  if (!stored || typeof stored !== "object") return clone(DEFAULT_SETTINGS);
  return {
    ...DEFAULT_SETTINGS,
    ...stored,
    units: { ...DEFAULT_SETTINGS.units, ...(stored.units || {}) },
  };
}

function clone(obj) {
  // structuredClone is available in all Vite targets, but keep a JSON fallback.
  try {
    return structuredClone(obj);
  } catch {
    return JSON.parse(JSON.stringify(obj));
  }
}

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clone(DEFAULT_SETTINGS);
    return mergeWithDefaults(JSON.parse(raw));
  } catch {
    return clone(DEFAULT_SETTINGS);
  }
}

/**
 * Pushes a few settings onto the document root so plain CSS can react to them
 * without every component subscribing to context. Called on every change.
 */
export function applySettingsToRoot(settings) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const scale = Number(settings.fontScale) || 1;
  root.style.setProperty("--font-scale", String(scale));
  root.dataset.density = settings.density || "comfortable";
  root.dataset.reduceMotion = settings.reduceMotion ? "true" : "false";
}

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(loadSettings);

  // Persist + live-apply on every change. No reload needed.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage full / unavailable — settings still live in memory */
    }
    applySettingsToRoot(settings);
  }, [settings]);

  // Keep multiple tabs in sync.
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_KEY) setSettings(loadSettings());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setSetting = useCallback((key, value) => {
    setSettings((prev) => {
      // Dotted keys (e.g. "units.altitude") update the nested object.
      if (key.includes(".")) {
        const [parent, child] = key.split(".");
        return {
          ...prev,
          [parent]: { ...(prev[parent] || {}), [child]: value },
        };
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(clone(DEFAULT_SETTINGS));
  }, []);

  const importSettings = useCallback((json) => {
    let parsed;
    try {
      parsed = typeof json === "string" ? JSON.parse(json) : json;
    } catch {
      throw new Error("That file is not valid settings JSON.");
    }
    setSettings(mergeWithDefaults(parsed));
  }, []);

  const exportSettings = useCallback(
    () => JSON.stringify(settings, null, 2),
    [settings]
  );

  const value = useMemo(
    () => ({
      settings,
      setSetting,
      resetSettings,
      importSettings,
      exportSettings,
    }),
    [settings, setSetting, resetSettings, importSettings, exportSettings]
  );

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used within a <SettingsProvider>");
  }
  return ctx;
}
